import { X } from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useBuilderDraft } from "@/hooks/useBuilderDraft";
import { useT } from "@/i18n";
import { PreviewModal } from "@/pages/builder/preview/PreviewModal";

/**
 * Read-only shareable preview of a draft.
 *
 * Route: ``/preview/:key`` — sits outside the ``<AppShell>`` so there's
 * no sidebar / header chrome, meant for sharing a link to stakeholders
 * before publish.
 *
 * Reuses ``useBuilderDraft`` to load the draft by key (same endpoint
 * the editor uses) and then renders the existing ``<PreviewModal>`` as
 * a full-screen page. The modal's Close button navigates back to the
 * draft list instead of closing an overlay.
 */
export function SharedPreviewPage(): JSX.Element {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { t } = useT();
  const draft = useBuilderDraft(key ?? "");
  const [opened, setOpened] = useState(false);

  // Auto-open once the draft loads — the modal state machine expects an
  // explicit ``open`` toggle.
  useEffect(() => {
    if (draft.status === "ready") setOpened(true);
  }, [draft.status]);

  if (!key) {
    return (
      <FullscreenMessage
        title={t("preview.notFoundTitle")}
        hint={t("preview.notFoundHint")}
      />
    );
  }

  if (draft.status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2 bg-neutral-100 text-sm text-neutral-500">
        <Spinner size={16} ariaLabel={t("common.loading")} />
        {t("common.loading")}
      </div>
    );
  }

  if (draft.status === "error") {
    return (
      <FullscreenMessage
        title={t("preview.loadErrorTitle")}
        hint={draft.error ?? undefined}
        actionLabel={t("common.retry")}
        onAction={() => window.location.reload()}
      />
    );
  }

  return (
    <PreviewModal
      open={opened}
      draft={draft}
      onClose={() => navigate("/")}
    />
  );
}

function FullscreenMessage({
  title,
  hint,
  actionLabel,
  onAction,
}: {
  title: string;
  hint?: string;
  actionLabel?: string;
  onAction?: () => void;
}): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-neutral-100 px-6 text-center">
      <X size={32} className="text-neutral-300" />
      <div className="text-lg font-semibold text-neutral-700">{title}</div>
      {hint && <div className="max-w-md text-sm text-neutral-500">{hint}</div>}
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="mt-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
